'use client';

import React from 'react';
import Link from 'next/link';
import AppLogo from './ui/AppLogo';
import {
  LayoutDashboard,
  Briefcase,
  ShoppingCart,
  FileText,
  Receipt,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  currentPath: string;
  onCollapse: () => void;
  onMobileClose: () => void;
}

const navItems = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard, badge: null },
  { label: 'Jobs', href: '/jobs', icon: Briefcase, badge: '3' },
  { label: 'Purchase Orders', href: '/purchase-order-management', icon: ShoppingCart, badge: '5' },
  { label: 'Sales Orders', href: '/sales-order-management', icon: FileText, badge: null },
  { label: 'Invoices', href: '/invoice-management', icon: Receipt, badge: '7' },
];

export default function Sidebar({ collapsed, mobileOpen, currentPath, onCollapse, onMobileClose }: SidebarProps) {
  const isActive = (href: string) => {
    if (href === '/') return currentPath === '/';
    return currentPath.startsWith(href);
  };

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col flex-shrink-0 relative z-10 transition-all duration-300 ease-in-out"
        style={{
          width: collapsed ? '64px' : '228px',
          backgroundColor: 'var(--ink)',
        }}
      >
        {/* Logo area */}
        <div
          className="flex items-center h-16 px-3 border-b flex-shrink-0"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          {collapsed ? (
            <div className="flex items-center justify-center w-full">
              <AppLogo size={28} />
            </div>
          ) : (
            <div className="flex items-center gap-2 overflow-hidden">
              <AppLogo size={28} />
              <span
                className="font-semibold text-lg tracking-tight text-white whitespace-nowrap"
                style={{ letterSpacing: '-0.01em' }}
              >
                Settle
              </span>
            </div>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={`nav-${item.href}`}
                href={item.href}
                className={`sidebar-nav-item${active ? ' active' : ''}`}
                title={collapsed ? item.label : undefined}
              >
                <Icon
                  size={18}
                  className={`nav-icon flex-shrink-0 ${active ? 'text-teal' : ''}`}
                />
                {!collapsed && (
                  <>
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.badge && (
                      <span
                        className="ml-auto text-xs font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: active ? 'rgba(18,148,107,0.3)' : 'rgba(255,255,255,0.12)',
                          color: active ? '#12946B' : 'rgba(255,255,255,0.7)',
                        }}
                      >
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div
          className="px-2 py-3 border-t flex-shrink-0"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          {!collapsed && (
            <div className="px-3 py-2 mb-2 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
              <p className="text-xs font-medium text-white/90 truncate">Marcus Webb</p>
              <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.45)' }}>Finance Team</p>
            </div>
          )}
          <button
            onClick={onCollapse}
            className="sidebar-nav-item w-full justify-center"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : (
              <>
                <ChevronLeft size={16} />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 flex flex-col lg:hidden transition-transform duration-300 ease-in-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ width: '228px', backgroundColor: 'var(--ink)' }}
      >
        <div
          className="flex items-center justify-between h-16 px-4 border-b flex-shrink-0"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-lg text-white" style={{ letterSpacing: '-0.01em' }}>
              Settle
            </span>
          </div>
          <button onClick={onMobileClose} className="text-white/60 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={`mobile-nav-${item.href}`}
                href={item.href}
                className={`sidebar-nav-item${active ? ' active' : ''}`}
                onClick={onMobileClose}
              >
                <Icon size={18} className={`nav-icon flex-shrink-0 ${active ? 'text-teal' : ''}`} />
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge && (
                  <span
                    className="ml-auto text-xs font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.7)' }}
                  >
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="px-3 py-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="px-3 py-2 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
            <p className="text-xs font-medium text-white/90">Marcus Webb</p>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>Finance Team</p>
          </div>
        </div>
      </aside>
    </>
  );
}